import React, { memo } from 'react'
import { Skeleton, Timeline, Alert, Typography } from 'antd'
import { useMediaQuery } from 'react-responsive'
import {
  InfoOutlined,
  CheckSquareOutlined,
  FileOutlined,
  BookOutlined,
  RollbackOutlined,
  RetweetOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  DollarOutlined,
} from '@ant-design/icons'
import TimelineItem from './activity-timeline-item/activity-timeline-Item'
import '../styles/index.css'
import { ActivityData } from '../types/types'
import { classNames } from '../../../../lib/classNames/classNames'
import ActivityTimelineSpace from './activity-timeline-item/activity-timeline-space/activity-timeline-space'
import moment from 'moment'
import { TimelineContainer as StyledTimelineContainer } from './styles'
import { HeaderTitle } from '../../../../views/order-execution-plan/styles'
import DotIcon from '../lib/components/dot-icon/diot-icon'
import EmptyDot from '../lib/components/empty-dot/empty-dot'
import { AvitoStyle } from '../styles/styles'

interface ActivityTimeLineProps extends ActivityData {
  className?: string
  isLoading?: boolean
  error?: string
  emptyMessage?: string
  hideHeader?: boolean
}

const { Text } = Typography

const ActivityTimeLine: React.FC<ActivityTimeLineProps> = memo((props: ActivityTimeLineProps) => {
  const { className, items, isLoading, error, emptyMessage, hideHeader } = props
  const isMobile = useMediaQuery({ maxWidth: 768 })

  if (isLoading) {
    return <Skeleton active avatar />
  }

  if (!isLoading && error) {
    return <Alert message="При загрузке ленты произошла ошибка" description={error} type="error" />
  }

  if (!isLoading && !error && (!items || !items?.length)) {
    return <Text>{emptyMessage || 'Лента активности пуста'}</Text>
  }

  const dateToFormat = (value: string) => {
    if ('planned' === value) {
      return 'Запланировано'
    }
    return moment.unix(Number(value)).calendar(null, {
      sameDay: '[Сегодня]',
      nextDay: '[Завтра]',
      nextWeek: '[Запланировано на следущей неделе]',
      lastDay: '[Вчера]',
      lastWeek: 'D MMMM',
      sameElse: 'D MMMM',
      // sameElse: 'DD MMMM YYYY',
    })
  }

  const renderDot = (activityItem: any) => {
    const { type, data, date } = activityItem || {}
    switch (type) {
      case 'stage':
        return (
          <DotIcon>
            <InfoOutlined />
          </DotIcon>
        )
      case 'task': {
        const now = moment().valueOf()
        const dotColor = parseInt(date) <= now ? '#2fc4f5' : '#97c807'
        return (
          <DotIcon>
            <CheckSquareOutlined style={{ color: dotColor }} />
          </DotIcon>
        )
      }
      case 'file':
        return (
          <DotIcon>
            <FileOutlined style={{ fontSize: '16px' }} />
          </DotIcon>
        )
      case 'note': {
        const noteType = data?.noteType
        if (noteType === 'region-change')
          return (
            <DotIcon>
              <InfoOutlined />
            </DotIcon>
          )
        if (noteType === 'manager-change')
          return (
            <DotIcon>
              <RollbackOutlined />
            </DotIcon>
          )
        if (noteType === 'repeated-appeal')
          return (
            <DotIcon>
              <RetweetOutlined />
            </DotIcon>
          )
        return (
          <DotIcon>
            <BookOutlined />
          </DotIcon>
        )
      }
      case 'info':
        return (
          <DotIcon>
            <ClockCircleOutlined />
          </DotIcon>
        )
      case 'call':
        return (
          <DotIcon>
            <PhoneOutlined />
          </DotIcon>
        )
      case 'payment': {
        return (
          <DotIcon dotColor={'#67c364'}>
            <DollarOutlined />
          </DotIcon>
        )
      }
      case 'avito':
        return (
          <DotIcon>
            <AvitoStyle>
              <svg
                width="25"
                height="25"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10.595" cy="5.225" r="3.325" fill="#965EEB"></circle>
                <circle cx="22.245" cy="7.235" r="7.235" fill="#0AF"></circle>
                <circle cx="8.9" cy="18.6" r="8.9" fill="#04E061"></circle>
                <circle cx="24.325" cy="21.005" r="5.375" fill="#FF4053"></circle>
              </svg>
            </AvitoStyle>
          </DotIcon>
        )
      default:
        return undefined
    }
  }

  return (
    <StyledTimelineContainer>
      {!hideHeader && <HeaderTitle>Лента событий</HeaderTitle>}
      <Timeline
        className={classNames('actions-time-line', {}, [className])}
        style={
          isMobile
            ? {
                minWidth: 'unset',
                maxWidth: '100%',
                width: '100%',
              }
            : {
                minWidth: '400px',
                maxWidth: '620px',
              }
        }
        items={
          items?.flatMap(activityDataItems => {
            const groupHeader = {
              key: `space-${activityDataItems.date}`,
              dot: <EmptyDot />,
              children: <ActivityTimelineSpace text={dateToFormat(activityDataItems.date)} />,
            } as any
            const groupItems =
              activityDataItems?.items?.map((activityItem, index) => ({
                key: `${activityItem.type}${index}${activityItem.id}`,
                dot: renderDot(activityItem as any),
                children: (
                  <TimelineItem
                    {...activityItem}
                    index={index}
                    scopeDate={activityDataItems.date}
                  />
                ),
              })) || []
            return [groupHeader, ...groupItems]
          }) || []
        }
      />
    </StyledTimelineContainer>
  )
})

ActivityTimeLine.displayName = 'ActivityTimeLine'

export default ActivityTimeLine
